import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flower2, ArrowLeft, Smartphone, Share, Download } from "lucide-react";
import { api, fmtDate, BACKEND_URL } from "@/lib/api";
import { BRAND, LOGO } from "@/lib/brand";

const Shell = ({ children, title, eyebrow }) => (
  <div className="min-h-screen bg-[#FAF8F5]">
    <header className="bg-white/90 backdrop-blur border-b border-[#E2DDD5] h-16 px-4 sm:px-8 flex items-center justify-between">
      <Link to="/"><img src={LOGO} alt={BRAND.name} className="h-9 w-auto" /></Link>
      <Link to="/" className="text-sm text-[#0D3B52] inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />Sito</Link>
    </header>
    <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#997316] font-semibold">{eyebrow}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[#0D3B52] mt-2 mb-8">{title}</h1>
      {children}
    </main>
  </div>
);

export function Cimitero() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/public/cimitero").then((r) => setItems(r.data)); }, []);
  return (
    <Shell eyebrow="In memoria" title="Cimitero virtuale">
      <p className="text-sm text-[#5C6B73] max-w-2xl -mt-4 mb-8">Un luogo dove i nostri compagni di vita continuano a essere ricordati. I clienti dell'agenzia possono lasciare una foto e una dedica dalla propria <Link to="/login" className="underline">area riservata</Link>.</p>
      {items.length === 0 ? <p className="text-[#5C6B73]" data-testid="cimitero-empty">Ancora nessuna dedica.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="cimitero-grid">
          {items.map((m, i) => (
            <div key={m.id} className="rounded-2xl overflow-hidden border border-[#E2DDD5] bg-white rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`memorial-${m.id}`}>
              <img src={`${BACKEND_URL}/api/public/cimitero/${m.id}/foto`} alt={m.pet_name} className="h-60 w-full object-cover" />
              <div className="p-5">
                <div className="flex items-center gap-2"><Flower2 className="h-4 w-4 text-[#B8860B]" /><p className="font-display text-2xl font-semibold text-[#0D3B52]">{m.pet_name}</p></div>
                {m.anni && <p className="text-xs text-[#997316] font-semibold mt-0.5">{m.anni}</p>}
                {m.dedica && <p className="text-sm text-[#1C2A33] italic mt-3">"{m.dedica}"</p>}
                <p className="text-xs text-[#5C6B73] mt-3">— {m.owner_name} · {fmtDate(m.created_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

export function ScaricaApp() {
  const [prompt, setPrompt] = useState(null);
  useEffect(() => { const h = (e) => { e.preventDefault(); setPrompt(e); }; window.addEventListener("beforeinstallprompt", h); return () => window.removeEventListener("beforeinstallprompt", h); }, []);
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  return (
    <Shell eyebrow="App" title="Scarica l'app">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="rounded-3xl bg-[#0D3B52] text-white p-8 grain">
          <Smartphone className="h-10 w-10 text-[#D4AF37]" />
          <p className="font-display text-3xl mt-4">{BRAND.name}</p>
          <p className="text-white/70 text-sm mt-2">Prenota i ritiri, segui la cremazione teleassistita, scarica il video ricordo, sfoglia il catalogo e visita il Cimitero virtuale. Funziona su Android, iPhone e PC, senza store.</p>
          {prompt && <button onClick={() => prompt.prompt()} className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-[#0D3B52] px-6 py-3 font-semibold" data-testid="app-install-button"><Download className="h-5 w-5" />Installa ora</button>}
          <Link to="/login" className="mt-6 ml-0 sm:ml-3 inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 font-medium" data-testid="app-login-link">Accedi all'area riservata</Link>
        </div>
        <div className="space-y-4 text-sm text-[#1C2A33]">
          <div className="rounded-2xl border border-[#E2DDD5] bg-white p-5"><p className="font-semibold text-[#0D3B52]">Android (Chrome)</p><p className="mt-1">Apri questa pagina, tocca il menu ⋮ in alto a destra e scegli <strong>“Installa app”</strong> o <strong>“Aggiungi a schermata Home”</strong>. L'icona dell'agenzia comparirà tra le tue app.</p></div>
          <div className={`rounded-2xl border bg-white p-5 ${ios ? "border-[#D4AF37]" : "border-[#E2DDD5]"}`}><p className="font-semibold text-[#0D3B52]">iPhone / iPad (Safari)</p><p className="mt-1">Tocca il pulsante <Share className="inline h-4 w-4" /> <strong>Condividi</strong> in basso e poi <strong>“Aggiungi alla schermata Home”</strong>.</p></div>
          <div className="rounded-2xl border border-[#E2DDD5] bg-white p-5"><p className="font-semibold text-[#0D3B52]">PC (Chrome / Edge)</p><p className="mt-1">Clicca l'icona <strong>“Installa”</strong> nella barra dell'indirizzo, oppure menu ⋮ → “Installa {BRAND.name}”.</p></div>
          <p className="text-xs text-[#5C6B73]">Per accedere usa l'email o il numero di cellulare e la password che ti ha comunicato l'agenzia.</p>
        </div>
      </div>
    </Shell>
  );
}
